import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Calendar } from 'lucide-react';
import axios from 'axios';
import Breadcrumbs from '../components/layout/Breadcrumbs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const { language, t } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      // Use placeholder posts if API fails
      setPosts([
        {
          id: '1',
          slug: 'seo-trends-2024',
          title_nl: 'SEO Trends voor 2024',
          title_en: 'SEO Trends for 2024',
          excerpt_nl: 'De belangrijkste SEO ontwikkelingen waar je dit jaar rekening mee moet houden.',
          excerpt_en: 'The most important SEO developments to consider this year.',
          category: 'Strategy',
          image_url: 'https://images.unsplash.com/photo-1702315139575-ba243e9602a0?crop=entropy&cs=srgb&fm=jpg&q=85',
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          slug: 'local-seo-guide',
          title_nl: 'Complete Local SEO Gids',
          title_en: 'Complete Local SEO Guide',
          excerpt_nl: 'Alles wat je moet weten over lokale vindbaarheid in Google.',
          excerpt_en: 'Everything you need to know about local visibility in Google.',
          category: 'Local SEO',
          image_url: 'https://images.unsplash.com/photo-1749631934602-13b05524e688?crop=entropy&cs=srgb&fm=jpg&q=85',
          created_at: new Date().toISOString()
        },
        {
          id: '3',
          slug: 'geo-optimization',
          title_nl: 'GEO: Optimaliseren voor AI',
          title_en: 'GEO: Optimizing for AI',
          excerpt_nl: 'Hoe bereid je je website voor op ChatGPT en andere AI-zoekmachines?',
          excerpt_en: 'How to prepare your website for ChatGPT and other AI search engines?',
          category: 'GEO',
          image_url: 'https://images.unsplash.com/photo-1758873268998-2f77c2d38862?crop=entropy&cs=srgb&fm=jpg&q=85',
          created_at: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="section-spacing bg-white">
        <div className="container-kesult">
          <div className="max-w-3xl">
            <span className="badge-kesult mb-6 inline-block">{t('nav.blog')}</span>
            <h1 className="heading-1 mb-6" data-testid="blog-title">
              {t('blog.title')}
            </h1>
            <p className="body-large text-[#71717A]">
              {t('blog.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <Breadcrumbs />

      {/* Blog Grid */}
      <section className="section-spacing bg-[#F1F0EF]" data-testid="blog-grid">
        <div className="container-kesult">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border-2 border-[#F1F0EF] rounded-[2rem] animate-pulse overflow-hidden">
                  <div className="aspect-video bg-gray-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-gray-200 w-1/4" />
                    <div className="h-6 bg-gray-200 w-3/4" />
                    <div className="h-4 bg-gray-200 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link 
                  key={post.id}
                  to={`${basePath}/blog/${post.slug}`}
                  className="bg-white border-2 border-[#F1F0EF] rounded-[2rem] hover:border-[#4286F4] transition-all duration-300 group overflow-hidden"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={post.image_url || 'https://images.unsplash.com/photo-1702315139575-ba243e9602a0?crop=entropy&cs=srgb&fm=jpg&q=85'}
                      alt={language === 'nl' ? post.title_nl : post.title_en}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="badge-kesult text-[10px]">{post.category}</span>
                      <span className="text-xs text-[#71717A] flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                    <h2 className="heading-3 mb-3">
                      {language === 'nl' ? post.title_nl : post.title_en}
                    </h2>
                    <p className="text-[#71717A] text-sm mb-4">
                      {language === 'nl' ? post.excerpt_nl : post.excerpt_en}
                    </p>
                    <div className="flex items-center gap-2 text-[#4286F4] font-semibold text-sm uppercase tracking-wide">
                      {t('blog.readMore')}
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {posts.length === 0 && !loading && (
            <div className="text-center py-16">
              <p className="text-[#71717A]">
                {language === 'nl' ? 'Nog geen artikelen.' : 'No articles yet.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-[#4286F4] text-white">
        <div className="container-kesult text-center">
          <h2 className="heading-2 mb-6">
            {language === 'nl' ? 'WIL JE MEER LEREN?' : 'WANT TO LEARN MORE?'}
          </h2>
          <p className="body-large opacity-90 max-w-2xl mx-auto mb-10">
            {language === 'nl'
              ? 'Plan een intake call en ontdek hoe ik jouw SEO kan verbeteren.'
              : 'Schedule an intake call and discover how I can improve your SEO.'}
          </p>
          <Link to={`${basePath}/intake`}>
            <Button className="bg-white text-[#4286F4] hover:bg-[#F1F0EF] font-bold uppercase tracking-wider px-12 py-4 text-lg border-2 border-white rounded-2xl">
              {t('hero.cta')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
